import React, { useState, useEffect, useRef } from 'react';
import { Editor, Path, Node } from 'slate';
import { ReactEditor, useSlateStatic, useSelected, useFocused } from 'slate-react';
import { nanoid } from 'nanoid';

import HelptapEditor from '../../helpers';

import { initializeResizer } from './resizer';

export function Column(props) {

  const { attributes, children, element } = props;

  const editor = useSlateStatic();
  const selected = useSelected();
  const focused = useFocused();

  const columnMainReference = useRef(null);
  const columnResizeHandleReference = useRef(null);
  const isResizingRef = useRef(false);
  const elementPathReference = Editor.pathRef(editor, ReactEditor.findPath(editor, element));
  const columns = Editor.node(editor, Path.parent(elementPathReference.current))[0];

  const [rootState, setRootState] = useState({
    width: element.width,
    nextSiblingNewWidth: 0,
    nextSiblingWidthDelta: 0
  });

  useEffect(function onLoad() {

    columnMainReference.current = ReactEditor.toDOMNode(editor, element);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onPropsChange() {

    return tryInitializeResizer();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);

  useEffect(function onWidthChange() {
    if (elementPathReference.current === null) {
      return;
    }
    HelptapEditor.multipleColumns.updateColumnWidth(editor, elementPathReference.current, rootState.width, rootState.nextSiblingWidthDelta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rootState.width]);

  function tryInitializeResizer() {

    if (HelptapEditor.isNextNodeExists(editor, elementPathReference.current) === false) {
      return;
    }

    var columnSpacing = columns.properties?.columnSpacing || 5;
    var nextSiblingPath = Path.next(elementPathReference.current);
    var nextSiblingElement = Node.get(editor, nextSiblingPath);

    var initialWidths = {
      targetWidth: element.width,
      nextSiblingWidth: nextSiblingElement.width
    };

    var uninitResizer = initializeResizer(
      columnMainReference.current,
      columnResizeHandleReference.current,
      handleWidthChange,
      handleResizeStart,
      handleResizeEnd,
      columnSpacing,
      initialWidths
    );

    return function onUnload() {
      if (isResizingRef.current === true) {
        return;
      }
      uninitResizer();
      uninitialize();
    };

  }

  function handleWidthChange(data) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        width: data.targetNewWidth,
        nextSiblingNewWidth: data.nextSiblingNewWidth,
        nextSiblingWidthDelta: data.nextSiblingWidthDelta
      };

    });

  }

  function handleResizeStart() {
    isResizingRef.current = true;
    HelptapEditor.clearSelection(editor);
  }

  function handleResizeEnd() {
    isResizingRef.current = false;
  }

  function uninitialize() {

    elementPathReference.unref();

  }

  function isLastColumn() {
    return HelptapEditor.isNextNodeExists(editor, elementPathReference.current) === false;
  }

  function tryRenderResizeHandle() {

    if (isLastColumn() === true) {
      return;
    }

    const attributes = {
      className: 'column-width-resize-handle',
      contentEditable: false,
      ref: columnResizeHandleReference,
      style: {
        right: `-${(columns.properties?.columnSpacing || 5)/2 + 1.5}px`
      }
    };

    return <div {...attributes} />;

  }

  function renderColumn() {

    const containerAttributes = {
      className: 'column-content-container'
    };

    return <React.Fragment>
      <div {...containerAttributes}>
        {children}
      </div>
      {tryRenderResizeHandle()}
    </React.Fragment>;

  }

  function renderMain() {

    /* beautify ignore:start */
    const columnId = element.properties?.id || nanoid();
    /* beautify ignore:end */

    const elementAttributes = {
      ...attributes,
      suppressContentEditableWarning: true,
      className: `column-${columnId} column ${selected && focused ? 'selected' : ''}`,
    };

    return <div {...elementAttributes}>
      <style>{`
        .column-${columnId} {
          width: ${element.width}%;
        }
      `}</style>
      {renderColumn()}
    </div>;
  }

  return renderMain();
}